import clsx from 'clsx';
import AppRate from 'common/components/rate.component';
import { APP_NAME } from 'config/constants/api.constants';
import { RouteName } from 'config/constants/route.constants';
import { CatalogTranscription } from 'config/constants/transcription.constant';
import CatalogUtils from 'config/utils/catalog.util';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'store/models/auth.model';
import { Cart } from 'store/models/cart.model';
import { Product, ProductVariant } from 'store/models/catalog.model';
import { User } from 'store/models/user.model';
import ProductTags from '../../product-tags.component';
import CardControls from '../card-controls.component';
import styles from './style.module.scss';

type ProductCardProps = {
  product: Product;
  hoverable?: boolean;
  cart: Cart;
  user: User;
  auth: Auth;
};

const ProductCard: React.FC<ProductCardProps> = ({ product, hoverable, cart, user, auth }) => {
  const navigate = useNavigate();
  const hasStock = useMemo(() => CatalogUtils.checkProductStock(product), [product]);
  const lowerVariant = useMemo(() => CatalogUtils.getLowerValueVariant(product), [product]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(() =>
    CatalogUtils.getSelectedVariantForProduct(product, cart.items),
  );

  const hasVotes = Number(product.votes) > 0;
  const reputation = hasVotes ? CatalogUtils.getProductReputation(product.votes, product.totalVotes ?? 0) : 0;

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = CatalogUtils.DEFAULT_PRODUCT_IMAGE;
  };

  const handleProductImage = (cardProduct: Product, variant?: ProductVariant): React.ReactNode => {
    const imagesToUse = variant?.images ?? cardProduct.variant?.[0]?.images ?? [];
    const findMainPicture = CatalogUtils.getMainProductImage(imagesToUse);
    const desktopSrc = findMainPicture?.desktopImage ?? CatalogUtils.DEFAULT_PRODUCT_IMAGE;
    const mobileSrc = findMainPicture?.mobileImage ?? desktopSrc;
    const imgText = `${APP_NAME} - ${cardProduct.name}`;

    return (
      <div className={styles['product-card-image-container']}>
        {cardProduct.isNew && <span className={styles['new-badge']}>{CatalogTranscription.PRODUCT_NEW}</span>}
        <picture>
          <source srcSet={mobileSrc} media='(max-width: 768px)' />
          <img
            alt={imgText}
            title={imgText}
            src={desktopSrc}
            onError={handleImageError}
            loading='lazy'
            className={styles['product-card-image']}
          />
        </picture>
      </div>
    );
  };

  const goToProductDetails = (cardProduct: Product) => {
    const productUrlPath = RouteName.PRODUCT_DETAILS.replace(`:productSlug`, `${cardProduct.slug}`);
    navigate(productUrlPath);
  };

  useEffect(() => {
    setSelectedVariant(CatalogUtils.getSelectedVariantForProduct(product, cart.items));
  }, [product, cart.items, lowerVariant]);

  return (
    <article className={clsx(styles['product-card'], hoverable && styles['hoverable'])} onClick={() => goToProductDetails(product)}>
      {handleProductImage(product, selectedVariant)}

      <div className={styles['product-card-body']}>
        <h3 className={styles['product-name']}>{product.name}</h3>

        {hasVotes && (
          <div className={styles['rating-row']}>
            <AppRate value={reputation} disabled allowHalf allowClear={false} additionalStyles={{ fontSize: 12 }} />
            <span className={styles['rating-value']}>{reputation.toFixed(1)}</span>
            <span className={styles['rating-count']}>
              {product.votes} {CatalogTranscription.PRODUCT_VOTES}
            </span>
          </div>
        )}

        <div className={styles['price-row']}>
          <div className={styles['price-main']}>
            <span className={styles['price-label']}>{CatalogTranscription.PRODUCT_PRICE_FROM}</span>
            <span className={styles['price-amount']}>{selectedVariant?.priceFormatted || '-'}</span>
          </div>
          {selectedVariant?.priceFormatted && (
            <span className={styles['price-unit']}>
              {selectedVariant.priceFormatted} {CatalogTranscription.PRODUCT_PRICE_PER_UNIT}
            </span>
          )}
        </div>

        <ProductTags tags={product.tags} limit={CatalogUtils.MAX_VISIBLE_TAGS} reserveSpace />

        <div className={styles['card-actions']}>
          <CardControls
            favorites={user.favorites}
            product={product}
            cartItems={cart.items}
            isAuthenticated={auth.isAuthenticated}
            hasStock={hasStock}
            userProfile={user}
            selectedVariant={selectedVariant}
            withFavoriteButton
            favoriteVariant='icon'
          />
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
